import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ReceiptLong as InvoiceIcon, Search as SearchIcon } from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import invoiceService from '../../../../services/invoiceService';
import venueSupplierService from '../../../../services/venueSupplierService';
import InvoiceDetailPanel from '../../../../components/venue/InvoiceDetailPanel';
import type { Invoice, InvoiceStatus, Supplier } from '../../../../types/venue';

const STATUS_OPTIONS: { value: InvoiceStatus | ''; label: string }[] = [
  { value: '',      label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open',  label: 'Open' },
  { value: 'void',  label: 'Void' },
];

const STATUS_STYLE: Record<InvoiceStatus, { bg: string; color: string }> = {
  draft: { bg: '#f3f4f6', color: '#374151' },
  open:  { bg: '#dafbe1', color: '#1a7f37' },
  void:  { bg: '#fde8e8', color: '#b91c1c' },
};

const InvoiceList: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter]     = useState<InvoiceStatus | ''>('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [invoiceSearch, setInvoiceSearch]   = useState('');

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [invoices, setInvoices]     = useState<Invoice[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const [supplierMap, setSupplierMap]   = useState<Map<string, string>>(new Map());
  const [suppliers, setSuppliers]       = useState<Supplier[]>([]);

  // ── Detail panel ─────────────────────────────────────────────────────────────
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  // ── Load suppliers ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueSupplierService.listVenueSuppliers(venueId, 1, 500).then(result => {
      const list: Supplier[] = result.data ?? [];
      setSuppliers(list);
      setSupplierMap(new Map(list.map(s => [s._id, s.name])));
    }).catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Load invoices ─────────────────────────────────────────────────────────────
  const loadInvoices = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await invoiceService.getInvoices(
        venueId,
        page + 1,
        pageSize,
        statusFilter || undefined,
        supplierFilter || undefined,
      );
      setInvoices(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to load invoices';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, statusFilter, supplierFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as InvoiceStatus | '');
    setPage(0);
  };

  const handleSupplierChange = (value: string) => {
    setSupplierFilter(value);
    setPage(0);
  };

  // ── Local search over loaded page ─────────────────────────────────────────────
  const visibleInvoices = invoiceSearch.trim()
    ? invoices.filter(inv =>
        inv.invoiceNumber.toLowerCase().includes(invoiceSearch.trim().toLowerCase()),
      )
    : invoices;

  // ── Formatting helpers ────────────────────────────────────────────────────────
  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount?: number | null, currency?: string | null) => {
    if (amount == null) return '—';
    const formatted = amount.toFixed(2);
    return currency ? `${currency} ${formatted}` : formatted;
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <InvoiceIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>Invoices</Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search invoice number"
          value={invoiceSearch}
          onChange={e => setInvoiceSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Supplier</InputLabel>
          <Select
            label="Supplier"
            value={supplierFilter}
            onChange={e => handleSupplierChange(e.target.value)}
          >
            <MenuItem value="">All suppliers</MenuItem>
            {suppliers.map(s => (
              <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={e => handleStatusChange(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {['Invoice #', 'Supplier', 'Status', 'Invoice Date', 'Total Amount', 'PO Reference'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      py: 1.5,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">Loading invoices…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : visibleInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <InvoiceIcon sx={{ fontSize: 44, mb: 1, opacity: 0.25 }} />
                      <Typography variant="body2">
                        {statusFilter || supplierFilter || invoiceSearch
                          ? 'No invoices match the current filters.'
                          : 'No invoices yet.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleInvoices.map(inv => {
                  const style = STATUS_STYLE[inv.status];
                  return (
                    <TableRow
                      key={inv._id}
                      hover
                      onClick={() => setViewingInvoice(inv)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.82rem">
                          {inv.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {supplierMap.get(inv.supplierId) ?? (
                            <span style={{ color: theme.palette.text.disabled }}>—</span>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            backgroundColor: style.bg,
                            color: style.color,
                          }}
                        >
                          {inv.status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(inv.invoiceDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatAmount(inv.totalAmount, inv.currencyCode)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {inv.purchaseOrderId ? (
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.78rem" color="text.secondary">
                            {inv.purchaseOrderId.slice(-8).toUpperCase()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Paper>

      {/* Detail panel — opened on row click */}
      {viewingInvoice && (
        <InvoiceDetailPanel
          open
          onClose={() => setViewingInvoice(null)}
          onSuccess={() => { loadInvoices(); setViewingInvoice(null); }}
          invoice={viewingInvoice}
          venueId={venueId}
          supplierName={supplierMap.get(viewingInvoice.supplierId)}
        />
      )}
    </Box>
  );
};

export default InvoiceList;
